import { useEffect, useRef, useState } from "react";
import {
  SORT_KEY_OPTIONS,
  sortOrderLabel,
  type TicketSortKey,
  type TicketSortOrder,
} from "../constants.ts";

function SortChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="support-filter-chip"
      onClick={onClick}
      style={{
        borderColor: active ? "var(--primary)" : "var(--border)",
        background: active ? "var(--primary)" : "transparent",
        color: active ? "#fff" : "var(--text)",
      }}
    >
      {children}
    </button>
  );
}

export function SortBar({
  sortKey,
  sortOrder,
  embedded = false,
  onSortKeyChange,
  onSortOrderChange,
}: {
  sortKey: TicketSortKey;
  sortOrder: TicketSortOrder;
  embedded?: boolean;
  onSortKeyChange: (key: TicketSortKey) => void;
  onSortOrderChange: (order: TicketSortOrder) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (evt: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(evt.target as Node)) {
        setOpen(false);
      }
    };
    const id = window.setTimeout(() => document.addEventListener("click", handler), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("click", handler);
    };
  }, [open]);

  const toggleOpen = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setOpen((v) => !v);
  };

  const keyLabel = SORT_KEY_OPTIONS.find((o) => o.key === sortKey)?.label || sortKey;
  const orderLabel = sortOrderLabel(sortKey, sortOrder);
  const summary = `${keyLabel}・${orderLabel}`;

  const panel = (
    <>
      <div className="support-filter-bar__groups">
        <div className="support-filter-bar__group">
          <span className="support-filter-bar__label">項目</span>
          {SORT_KEY_OPTIONS.map((o) => (
            <SortChip
              key={o.key}
              active={sortKey === o.key}
              onClick={() => onSortKeyChange(o.key)}
            >
              {o.label}
            </SortChip>
          ))}
        </div>

        <div className="support-filter-bar__divider" aria-hidden />

        <div className="support-filter-bar__group">
          <span className="support-filter-bar__label">順序</span>
          <SortChip active={sortOrder === "desc"} onClick={() => onSortOrderChange("desc")}>
            {sortOrderLabel(sortKey, "desc")}
          </SortChip>
          <SortChip active={sortOrder === "asc"} onClick={() => onSortOrderChange("asc")}>
            {sortOrderLabel(sortKey, "asc")}
          </SortChip>
        </div>
      </div>

      <div className="support-filter-bar__expanded-actions">
        <button type="button" className="support-filter-bar__toggle" onClick={toggleOpen}>
          ▴ 閉じる
        </button>
      </div>
    </>
  );

  return (
    <div
      ref={rootRef}
      className={
        "support-filter-bar support-sort-bar" +
        (open ? " support-filter-bar--open" : "") +
        (embedded ? " support-filter-bar--embedded" : "")
      }
    >
      <div className="support-filter-bar__collapsed">
        <button
          type="button"
          className={
            "support-filter-bar__toggle support-filter-bar__toggle--lead" +
            (open ? " support-filter-bar__toggle--active" : "")
          }
          onClick={toggleOpen}
          aria-expanded={open}
        >
          並び替え {open ? "▴" : "▾"}
        </button>
        <div className="support-filter-bar__badges">
          <span className="support-filter-bar__badge support-sort-bar__badge">{summary}</span>
        </div>
      </div>

      {open &&
        (embedded ? (
          <div className="support-filter-bar__popover" onClick={(e) => e.stopPropagation()}>
            {panel}
          </div>
        ) : (
          <div className="support-filter-bar__expanded">{panel}</div>
        ))}
    </div>
  );
}
